import { useState, useEffect } from "react";
import AgentLayout from "@/layouts/AgentLayout";
import { 
  useListAgentMembers, 
  useCreateAgentMember, 
  useUpdateAgentMember,
  useGetAgentMemberCareTeam,
  useAddAgentMemberCareTeam,
  useRemoveAgentMemberCareTeam,
  useSearchProvidersForCareTeam
} from "@/lib/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Search, 
  RefreshCcw, 
  Users, 
  UserPlus, 
  Pencil, 
  MapPin, 
  User, 
  UserCheck, 
  Plus, 
  Trash2, 
  Lock, 
  ShieldCheck, 
  Loader2,
  AlertCircle,
  CreditCard
} from "lucide-react";
import MemberIdCardModal from "@/components/agent/MemberIdCardModal";

import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/ui/list-pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface MemberFormState {
  id?: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: string;
  dob: string;
  ssn: string;
  buildingNo: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  password?: string;
}

const initialFormState: MemberFormState = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  gender: "",
  dob: "",
  ssn: "",
  buildingNo: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  password: "Straightdoc123!"
};

export default function AgentMembers() {
  const [limit, setLimit] = useState(10);
  const [user, setUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { toast } = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<MemberFormState>(initialFormState);

  // Care Team Modal State
  const [careTeamModalOpen, setCareTeamModalOpen] = useState(false);
  const [selectedMemberForCareTeam, setSelectedMemberForCareTeam] = useState<any>(null);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [selectedProviderIdToAdd, setSelectedProviderIdToAdd] = useState("");

  // ID Card Modal State
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [selectedMemberForCard, setSelectedMemberForCard] = useState<any>(null);


  const createAgentMember = useCreateAgentMember();
  const updateAgentMember = useUpdateAgentMember();

  // Care Team Queries & Mutations
  const { data: careTeamProviders, isLoading: loadingCareTeam } = useGetAgentMemberCareTeam(
    selectedMemberForCareTeam?.id || selectedMemberForCareTeam?.userId || ""
  );

  const { data: searchProviderResults } = useSearchProvidersForCareTeam(providerSearchTerm);
  const addCareTeamMember = useAddAgentMemberCareTeam();
  const removeCareTeamMember = useRemoveAgentMemberCareTeam();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const { data: members, isLoading: loadingMembers } = useListAgentMembers();

  const handleOpenEnrollModal = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setModalOpen(true);
  };

  const handleOpenEditModal = (m: any) => {
    setIsEditing(true);
    setFormData({
      id: m.id || m.userId,
      firstName: m.firstName || (m.name ? m.name.split(' ')[0] : ""),
      middleName: m.middleName || "",
      lastName: m.lastName || (m.name ? m.name.split(' ').slice(1).join(' ') : ""),
      email: m.email || "",
      phone: m.phone || "",
      gender: m.gender || "",
      dob: m.dob || "",
      ssn: m.rawSsn || (m.ssn ? m.ssn.replace(/[^0-9]/g, '') : ""),
      buildingNo: m.buildingNo || "",
      street1: m.street1 || "",
      street2: m.street2 || "",
      city: m.city || "",
      state: m.state || "",
      zipCode: m.zipCode || ""
    });
    setModalOpen(true);
  };

  const handleOpenCareTeamModal = (m: any) => {
    setSelectedMemberForCareTeam(m);
    setProviderSearchTerm("");
    setSelectedProviderIdToAdd("");
    setCareTeamModalOpen(true);
  };

  const handleOpenCardModal = (m: any) => {
    setSelectedMemberForCard(m);
    setCardModalOpen(true);
  };


  const handleAddProviderToCareTeam = async () => {
    if (!selectedProviderIdToAdd || !selectedMemberForCareTeam) return;
    const memberId = selectedMemberForCareTeam.id || selectedMemberForCareTeam.userId;

    try {
      await addCareTeamMember.mutateAsync({
        memberId,
        providerId: selectedProviderIdToAdd
      });
      toast({
        title: "Care Team Updated",
        description: "Provider has been assigned to the member's Care Team."
      });
      setSelectedProviderIdToAdd("");
    } catch (err: any) {
      toast({
        title: "Failed to Add Provider",
        description: err.message || "Could not add provider to Care Team.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveProviderFromCareTeam = async (providerId: string) => {
    if (!selectedMemberForCareTeam) return;
    const memberId = selectedMemberForCareTeam.id || selectedMemberForCareTeam.userId;

    try {
      await removeCareTeamMember.mutateAsync({
        memberId,
        providerId
      });
      toast({
        title: "Care Team Provider Removed",
        description: "Provider was removed from the member's Care Team."
      });
    } catch (err: any) {
      toast({
        title: "Remove Failed",
        description: err.message || "Could not remove provider from Care Team.",
        variant: "destructive"
      });
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      toast({
        title: "Validation Error",
        description: "First name, last name, and email address are required.",
        variant: "destructive"
      });
      return;
    }

    try {
      if (isEditing && formData.id) {
        await updateAgentMember.mutateAsync({
          id: formData.id,
          data: {
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            gender: formData.gender,
            dob: formData.dob,
            ssn: formData.ssn,
            buildingNo: formData.buildingNo,
            street1: formData.street1,
            street2: formData.street2,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode
          }
        });
        toast({
          title: "Member Updated",
          description: `Patient personal information for ${formData.firstName} ${formData.lastName} has been updated.`
        });
      } else {
        await createAgentMember.mutateAsync(formData);
        toast({
          title: "Member Enrolled",
          description: `${formData.firstName} ${formData.lastName} has been enrolled successfully and linked to your agent account.`
        });
      }
      setModalOpen(false);
      setFormData(initialFormState);
    } catch (err: any) {
      toast({
        title: isEditing ? "Update Failed" : "Enrollment Failed",
        description: err.message || "An error occurred while saving patient information.",
        variant: "destructive"
      });
    }
  };

  const filteredMembers = members?.filter((m: any) => {
    const matchesSearch = 
      m.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.phone?.toLowerCase().includes(searchQuery.toLowerCase());
      
    if (!matchesSearch) return false;

    if (dateFilter !== "all" && m.joinedAt) {
      const joinedDate = new Date(m.joinedAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - joinedDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (dateFilter === "7" && diffDays > 7) return false;
      if (dateFilter === "30" && diffDays > 30) return false;
      if (dateFilter === "90" && diffDays > 90) return false;
      if (dateFilter === "custom") {
        if (customStartDate && joinedDate < new Date(customStartDate)) return false;
        if (customEndDate) {
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (joinedDate > endDate) return false;
        }
      }
    }
    
    return true;
  }) || [];

  const totalPages = Math.ceil(filteredMembers.length / pageSize);
  const totalCount = filteredMembers.length;
  const paginatedMembers = filteredMembers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const resetFilters = () => {
    setSearchQuery("");
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  return (
    <AgentLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">My Members</h1>
              <p className="text-muted-foreground text-sm">Enroll new members, update patient Care Teams, and manage personal information.</p>
            </div>
          </div>
          
          <Button
            onClick={handleOpenEnrollModal}
            className="bg-primary hover:bg-primary/90 text-white font-bold h-11 px-5 rounded-xl shadow-md flex items-center gap-2 shrink-0 self-start md:self-auto"
          >
            <UserPlus className="w-4 h-4" /> Enroll New Member
          </Button>
        </div>

        {/* Filter Row */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-white dark:bg-card p-4 rounded-2xl border border-border shadow-xs">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members by name, email, phone..."
              className="pl-9 bg-slate-50 dark:bg-slate-900 border-border rounded-xl text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-40 shrink-0">
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="bg-slate-50 dark:bg-slate-900 text-xs rounded-xl">
                  <SelectValue placeholder="Filter by date" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="custom">Custom Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dateFilter === "custom" && (
              <div className="flex items-center gap-2">
                <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="bg-slate-50 dark:bg-slate-900 text-xs rounded-xl w-36" />
                <span className="text-muted-foreground">-</span>
                <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="bg-slate-50 dark:bg-slate-900 text-xs rounded-xl w-36" />
              </div>
            )}
            {(searchQuery || dateFilter !== "all" || customStartDate || customEndDate) && (
              <Button variant="ghost" size="icon" onClick={resetFilters} title="Reset filters" className="rounded-xl">
                <RefreshCcw className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        </div>

        {/* Members Table */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-card rounded-2xl border border-border p-6 shadow-sm">
            {loadingMembers ? (
               <div className="space-y-3"><Skeleton className="w-full h-12" /><Skeleton className="w-full h-12" /><Skeleton className="w-full h-12" /></div>
            ) : filteredMembers.length > 0 ? (
              <div className="flex flex-col space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                      <tr>
                        <th className="px-4 py-3.5 rounded-tl-lg">Patient Name</th>
                        <th className="px-4 py-3.5">Email</th>
                        <th className="px-4 py-3.5">Phone</th>
                        <th className="px-4 py-3.5">DOB & Gender</th>
                        <th className="px-4 py-3.5">Joined Date</th>
                        <th className="px-4 py-3.5 text-right rounded-tr-lg">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {paginatedMembers.map((m: any) => (
                        <tr key={m.id} className="hover:bg-muted/50 transition-colors">
                          <td className="px-4 py-3.5 font-bold text-foreground">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                                {m.name ? m.name[0].toUpperCase() : 'M'}
                              </div>
                              <div>
                                <span>{m.name}</span>
                                {m.ssn && <span className="block text-[11px] text-muted-foreground font-mono">SSN: {m.ssn}</span>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-muted-foreground text-xs">{m.email}</td>
                          <td className="px-4 py-3.5 text-muted-foreground text-xs">{m.phone || "—"}</td>
                          <td className="px-4 py-3.5 text-muted-foreground text-xs">
                            {m.dob || "—"} {m.gender ? `(${m.gender})` : ''}
                          </td>
                          <td className="px-4 py-3.5 text-muted-foreground text-xs">
                            {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenCardModal(m)}
                                className="h-8 px-3 text-xs font-semibold text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-xl gap-1.5"
                              >
                                <CreditCard className="w-3.5 h-3.5" /> ID Card
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenCareTeamModal(m)}
                                className="h-8 px-3 text-xs font-semibold text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded-xl gap-1.5"
                              >
                                <UserCheck className="w-3.5 h-3.5" /> Care Team
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenEditModal(m)}
                                className="h-8 px-3 text-xs font-semibold text-primary border-primary/30 hover:bg-primary/5 rounded-xl gap-1.5"
                              >
                                <Pencil className="w-3.5 h-3.5" /> Edit Info
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="pt-2">
                    <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm py-12 text-center flex flex-col items-center justify-center">
                <Users className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="font-bold text-base text-foreground mb-1">No Members Found</p>
                <p className="text-xs text-muted-foreground mb-4">You haven't enrolled any members yet or no members match your search.</p>
                <Button onClick={handleOpenEnrollModal} size="sm" className="rounded-xl font-bold gap-1.5">
                  <UserPlus className="w-4 h-4" /> Enroll First Member
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Care Team Management Modal */}
        <Dialog open={careTeamModalOpen} onOpenChange={setCareTeamModalOpen}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                <UserCheck className="w-5 h-5 text-emerald-600" />
                Manage Care Team — {selectedMemberForCareTeam?.name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-2">
              {/* Subscription Lock Notice */}
              <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 flex items-start gap-3">
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  <p className="font-semibold">Subscription & Plan Protection</p>
                  <p className="mt-0.5 opacity-90">
                    Agents are authorized to manage member Care Team assignments and personal profile details. Member subscriptions and payment plans are protected and can only be modified directly by the member.
                  </p>
                </div>
              </div>

              {/* Add Provider Section */}
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border border-border rounded-xl space-y-3">
                <h4 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Plus className="w-4 h-4 text-primary" /> Assign New Provider to Care Team
                </h4>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search provider by name or specialty..."
                      value={providerSearchTerm}
                      onChange={e => setProviderSearchTerm(e.target.value)}
                      className="pl-9 h-10 text-xs rounded-xl"
                    />
                  </div>

                  <Select value={selectedProviderIdToAdd} onValueChange={setSelectedProviderIdToAdd}>
                    <SelectTrigger className="h-10 text-xs rounded-xl w-full sm:w-64 bg-white dark:bg-slate-900">
                      <SelectValue placeholder="Select Provider" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {searchProviderResults && searchProviderResults.length > 0 ? (
                        searchProviderResults.map((p: any) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.specialty})
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          {providerSearchTerm ? "No providers found" : "Type to search providers"}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={handleAddProviderToCareTeam}
                    disabled={!selectedProviderIdToAdd || selectedProviderIdToAdd === "none" || addCareTeamMember.isPending}
                    className="h-10 px-4 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-1.5 shrink-0"
                  >
                    {addCareTeamMember.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add Provider
                  </Button>
                </div>
              </div>

              {/* Current Care Team List */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
                  <span>Current Care Team Providers</span>
                  <span className="text-slate-400 font-normal">({careTeamProviders?.length || 0} assigned)</span>
                </h4>

                {loadingCareTeam ? (
                  <div className="space-y-2 py-4">
                    <Skeleton className="w-full h-12 rounded-xl" />
                    <Skeleton className="w-full h-12 rounded-xl" />
                  </div>
                ) : careTeamProviders && careTeamProviders.length > 0 ? (
                  <div className="space-y-2">
                    {careTeamProviders.map((provider: any) => (
                      <div 
                        key={provider.id || provider.providerId}
                        className="p-3.5 bg-white dark:bg-card border border-border rounded-xl flex items-center justify-between gap-3 shadow-2xs"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold flex items-center justify-center text-xs shrink-0">
                            <ShieldCheck className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-foreground">{provider.providerName}</p>
                            <p className="text-xs text-muted-foreground">{provider.specialty} • {provider.phone}</p>
                            <p className="text-[11px] text-slate-400 truncate max-w-sm">{provider.address}</p>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveProviderFromCareTeam(provider.providerId || provider.id)}
                          disabled={removeCareTeamMember.isPending}
                          className="h-8 px-2.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg gap-1.5 shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-dashed border-border">
                    <UserCheck className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-60" />
                    <p className="text-xs font-semibold text-foreground">No Care Team Providers Assigned</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Use the dropdown above to assign care providers to this member.</p>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Enroll / Edit Member Modal Dialog */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg font-bold">
                {isEditing ? (
                  <><Pencil className="w-5 h-5 text-primary" /> Edit Patient Personal Information</>
                ) : (
                  <><UserPlus className="w-5 h-5 text-primary" /> Enroll New Member / Patient</>
                )}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmitForm} className="space-y-5 mt-2">
              {/* Subscription Protection Badge in Edit Modal */}
              {isEditing && (
                <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
                    <Lock className="w-4 h-4 text-slate-400" />
                    Member Subscription Plan
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300">
                    Active (Member Managed)
                  </span>
                </div>
              )}

              {/* Personal Info Section */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                  <User className="w-4 h-4" /> Personal Identification
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-foreground">First Name *</Label>
                    <Input
                      required
                      value={formData.firstName}
                      onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Jane"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Middle Name</Label>
                    <Input
                      value={formData.middleName}
                      onChange={e => setFormData({ ...formData, middleName: e.target.value })}
                      placeholder="Marie"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Last Name *</Label>
                    <Input
                      required
                      value={formData.lastName}
                      onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Doe"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Email Address *</Label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jane.doe@example.com"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Phone Number</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 000-0000"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Date of Birth</Label>
                    <Input
                      type="date"
                      value={formData.dob}
                      onChange={e => setFormData({ ...formData, dob: e.target.value })}
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Gender</Label>
                    <Select value={formData.gender} onValueChange={v => setFormData({ ...formData, gender: v })}>
                      <SelectTrigger className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">Social Security Number (SSN)</Label>
                    <Input
                      type="text"
                      value={formData.ssn}
                      onChange={e => setFormData({ ...formData, ssn: e.target.value })}
                      placeholder="e.g. 1234 or XXX-XX-XXXX"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Residential Address Section */}
              <div className="space-y-3 pt-3 border-t border-border">
                <h4 className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> Residential Address
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <Label className="text-xs font-semibold text-foreground">Building / Apt No.</Label>
                    <Input
                      value={formData.buildingNo}
                      onChange={e => setFormData({ ...formData, buildingNo: e.target.value })}
                      placeholder="Apt 4B"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-foreground">Street Address Line 1</Label>
                    <Input
                      value={formData.street1}
                      onChange={e => setFormData({ ...formData, street1: e.target.value })}
                      placeholder="123 Health Ave"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-foreground">City</Label>
                    <Input
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Austin"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">State</Label>
                    <Input
                      value={formData.state}
                      onChange={e => setFormData({ ...formData, state: e.target.value })}
                      placeholder="TX"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-foreground">ZIP Code</Label>
                    <Input
                      value={formData.zipCode}
                      onChange={e => setFormData({ ...formData, zipCode: e.target.value })}
                      placeholder="78701"
                      className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 font-mono"
                    />
                  </div>
                </div>
              </div>

              {!isEditing && (
                <div className="pt-3 border-t border-border">
                  <Label className="text-xs font-semibold text-foreground">Temporary Account Password</Label>
                  <Input
                    type="text"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Default: Straightdoc123!"
                    className="mt-1 rounded-xl text-xs bg-slate-50 dark:bg-slate-900 font-mono"
                  />
                  <span className="text-[11px] text-muted-foreground mt-1 block">
                    The member can use this email and temporary password to log into their patient portal.
                  </span>
                </div>
              )}

              <Button
                type="submit"
                disabled={createAgentMember.isPending || updateAgentMember.isPending}
                className="w-full rounded-xl font-bold bg-primary hover:bg-primary/90 text-white h-11 shadow-md mt-4"
              >
                {createAgentMember.isPending || updateAgentMember.isPending ? (
                  "Saving Patient Information..."
                ) : isEditing ? (
                  "Save Personal Information Changes"
                ) : (
                  "Enroll Member & Link to Account"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        {/* Member ID Card Modal */}
        <MemberIdCardModal
          open={cardModalOpen}
          onOpenChange={setCardModalOpen}
          member={selectedMemberForCard}
          agentUser={user}
        />
      </div>
    </AgentLayout>
  );
}

